import React, { useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Card,
  Clickable,
  Icon,
  IconButton,
  P,
  Stack,
  Popover,
  Badge,
} from '@fashionnovateam/nova-ui-toolkit';
import { Tooltip } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';
import cn from 'classnames';
import VPLogo from '../../Assets/images/vp-logo.png';
import FNLogo from '../../Assets/images/fn-logo.png';
import { NavigationHeaderProps, NavigationUser } from './Props';
import { isExternalUser } from './utils';
import { UserRoleDisplay } from '../../Lib/Constants/roles';
import { useNavigation } from '../../Lib/Hooks/navigation';

const getMaxString = (text: string, maxValue: number) =>
  text.length < maxValue ? text : `${text.slice(0, maxValue - 4)} ...`;

const getName = (user?: NavigationUser) =>
  user ? `${user.first_name} ${user.last_name}` : '';

const getCompanyName = (user: NavigationUser, maxCharacters = 16) =>
  getMaxString(user.vendor?.company_name || getName(user), maxCharacters);

const getVendorId = (user: NavigationUser) =>
  getMaxString(user.vendor?.internal_id || '-', 21);

const getTitle = (user?: NavigationUser, maxCharacters = 16) =>
  user && isExternalUser(user)
    ? getCompanyName(user, maxCharacters)
    : 'Vendor Portal';

const getSubtitle = (user?: NavigationUser) => {
  if (!user) return '';
  return isExternalUser(user) ? getVendorId(user) : UserRoleDisplay[user.role];
};

const getBadge = (user?: NavigationUser) => {
  if (!user) return 'fn_internal';
  switch (user.role) {
    case 'VENDOR':
      return 'vendor';
    case 'VENDOR_STAFF':
      return 'vendor';
    case 'VENDOR_OPERATIONS':
      return 'vendor';
    case 'INFLUENCER':
      return 'influencer';
    default:
      return 'fn_internal';
  }
};

const calculateIsMobile = () => window.innerWidth < 1024;

const getPopoverPosition = ({
  isMobile,
  isCollapsed,
}: {
  isMobile: boolean;
  isCollapsed: boolean;
}): 'bottom-left' | 'right-bottom' | 'bottom-right' => {
  if (isMobile) return 'bottom-left';
  if (isCollapsed) return 'right-bottom';
  return 'bottom-right';
};

export function NavigationBarHeader(props: NavigationHeaderProps) {
  const { user, logout, onClose } = props;
  const { isCollapsed, setIsCollapsed } = useNavigation();
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(calculateIsMobile());
  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(calculateIsMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  const [copyState, setCopyState] = useState(false);

  const handleSubtitleClipboardCopy = () => {
    navigator.clipboard.writeText(getSubtitle(user));
    setCopyState(true);
  };

  return (
    <Popover position={getPopoverPosition({ isMobile, isCollapsed })}>
      <div
        className="relative h-14 min-h-14 w-full flex items-center border-b border-neutral-300"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {onClose && (
          <div className="absolute left-2 top-[50%] transform -translate-y-1/2 z-10">
            <IconButton
              label="Close"
              size="sm"
              icon={
                <Icon name="multiple-cross/multiple-cross-cancel-default" />
              }
              onClick={onClose}
              color="secondary"
            />
          </div>
        )}
        <AnimatePresence>
          {!isCollapsed && isHovering && !isMobile && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="absolute right-2 top-4"
            >
              <Clickable
                className="hover:bg-neutral-300 rounded-md p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCollapsed(true);
                }}
              >
                <Icon.SidebarRightOpen />
              </Clickable>
            </motion.div>
          )}
        </AnimatePresence>
        <Popover.Clickable
          className={cn(
            'w-full h-full',
            isHovering && 'bg-neutral-200',
            isCollapsed ? 'px-0' : 'px-4',
            onClose && 'pl-12',
          )}
        >
          <div className="flex-initial h-full flex items-center">
            <div
              className={cn(
                'flex items-center',
                isCollapsed ? 'justify-between w-full px-4' : 'gap-2',
              )}
            >
              <div
                className={cn('flex items-center', isCollapsed && 'flex-1')}
              />
              <Link to="/" className="flex justify-center">
                <img
                  className="rounded-md min-w-6 min-h-6 w-6 h-6"
                  alt="logo"
                  src={user && isExternalUser(user) ? FNLogo : VPLogo}
                />
              </Link>
              {!isCollapsed && (
                <div className="flex-col justify-center items-start gap-0.5 inline-flex max-w-[115px]">
                  <P size="xs" weight="semibold" className="w-full truncate">
                    {getTitle(user)}
                  </P>
                </div>
              )}
              <div
                className={cn(
                  'flex items-center',
                  isCollapsed && 'flex-1 justify-end',
                )}
              >
                <Icon.ChevronDown className="w-4 h-4" />
              </div>
            </div>
          </div>
        </Popover.Clickable>
        <Popover.Content className={cn('z-50', isMobile ? '-m-2.5' : 'm-1')}>
          <Card className="w-60 z-50">
            <Card.Header withHorizontalPadding>
              <P size="xsm" weight="medium">
                {getTitle(user, 36)}
              </P>
              <P size="xs" weight="normal" color="disabled">
                <Tooltip
                  placement="top"
                  title={
                    copyState ? 'Copied to Clipboard' : 'Copy to Clipboard'
                  }
                >
                  <Clickable onClick={handleSubtitleClipboardCopy}>
                    <P size="2xs">{getSubtitle(user)}</P>
                  </Clickable>
                </Tooltip>
              </P>
            </Card.Header>
            <Card.Body withHorizontalPadding>
              <div className="grid grid-cols-[min-content_1fr_auto] gap-2 items-center">
                <Avatar
                  name={getName(user)}
                  badgeType={getBadge(user)}
                  size="sm"
                  className="flex-shrink-0"
                />
                <P size="xsm" weight="normal" className="truncate">
                  {getName(user)}
                </P>
                {user?.role && (
                  <Badge color="neutral" contrast="low">
                    {UserRoleDisplay[user.role]}
                  </Badge>
                )}
              </div>
            </Card.Body>
            <Card.Footer withHorizontalPadding>
              <Clickable
                className="self-stretch hover:bg-neutral-100 px-3 py-2"
                onClick={logout}
              >
                <Stack direction="row" gap="sm">
                  <P size="sm" color="disabled">
                    <Icon name="log-out/log-out-right" />
                  </P>
                  <P size="xs">Logout</P>
                </Stack>
              </Clickable>
            </Card.Footer>
          </Card>
        </Popover.Content>
      </div>
    </Popover>
  );
}
